export type ObfuscatorVersion = "v1" | "v2" | "v3" | "v4";

export interface ObfuscatorOptions {
    version?: ObfuscatorVersion;
    key?: string;
}

export class Obfuscator {
    private readonly _v4Prefix = "cob:v4";
    private readonly _base64UrlAlphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";

    private readonly _randomKey = (): string =>
        (Date.now() / 1000 | 0).toString(16) +
        "xxxxxxxxxxxxxxxx".replace(/x/g, () => (Math.random() * 16 | 0).toString(16)).toLowerCase();

    /**
     * Preferred obfuscation API. Defaults to V4.
     */
    public encode(inputStr: string, options: ObfuscatorOptions = {}): string {
        const version = options.version ?? "v4";

        switch (version) {
            case "v1":
                return options.key === undefined ? this.obfuscate(inputStr) : this.obfuscate(inputStr, options.key);
            case "v2":
                return this.obfuscatev2(inputStr);
            case "v3":
                return this.obfuscatev3(inputStr, this._requireKey(options.key, "v3"));
            case "v4":
                return this.obfuscatev4(inputStr, this._requireKey(options.key, "v4"));
        }
    }

    /**
     * Preferred deobfuscation API. V4 payloads are self-identifying.
     */
    public decode(inputStr: string, options: ObfuscatorOptions = {}): string {
        const version = options.version ?? "v4";

        switch (version) {
            case "v1":
                return this.deobfuscate(inputStr, this._requireKey(options.key, "v1"));
            case "v2":
                return this.deobfuscatev2(inputStr);
            case "v3":
                return this.deobfuscatev3(inputStr, this._requireKey(options.key, "v3"));
            case "v4":
                return this.deobfuscatev4(inputStr, this._requireKey(options.key, "v4"));
        }
    }

    private _buildKeyValue(key: string): string {
        let kv = "";
        for (let i = 0; i < key.length; i++) kv += key.charCodeAt(i);
        return "l" + kv.split("").reverse().join("") + "l";
    }

    /**
     * V1 obfuscation - key-based character code encoding.
     * If no key is provided, a random key is generated and you cannot deobfuscate.
     * @param inputStr String to obfuscate.
     * @param key Optional key for reversibility.
     */
    public obfuscate(inputStr: string, key: string = this._randomKey()): string {
        const kv = this._buildKeyValue(key);
        let result = "";
        for (let i = 0; i < inputStr.length; i++) result += inputStr.charCodeAt(i) + kv;
        return result;
    }

    /**
     * V1 deobfuscation.
     * @param inputStr String to deobfuscate.
     * @param key Key used during obfuscation.
     */
    public deobfuscate(inputStr: string, key: string): string {
        const kv = this._buildKeyValue(key);
        const parts = inputStr.split(kv).filter((e) => e !== "");
        return parts.map((e) => String.fromCharCode(Number(e))).join("");
    }

    private _swapPairs(input: string): string {
        let result = "";
        for (let i = 0; i < input.length; i++) {
            if (i % 2 === 1) result += input[i] + input[i - 1];
            else if (i === input.length - 1) result += input[i];
        }
        return result;
    }

    /**
     * V2 obfuscation - pair-swapping with incremental key shifting.
     */
    public obfuscatev2(inputStr: string): string {
        const sortByLen = (a: string, b: string): string =>
            a.length > b.length ? "s" + b + a : a + b;

        const reformat = (text: string): string => {
            let result = "";
            for (let i = 0; i < text.length; i++) {
                if (i % 2 === 1) {
                    result += sortByLen(String(text.charCodeAt(i - 1)), String(text.charCodeAt(i)));
                    if (i !== text.length - 1) result += ":";
                }
                if (i % 2 === 0 && i === text.length - 1)
                    result += "e" + String(text.charCodeAt(i));
            }
            return result;
        };

        const increment = (input: string): string[] => {
            return input.split(":").map((e, i) => {
                if (e[0] !== "e" && e[0] !== "s") return String(Number(e) + (i + i * 2));
                return e;
            });
        };

        return increment(reformat(this._swapPairs(inputStr))).reduce((f, l) => f + ":" + l);
    }

    /**
     * V2 deobfuscation.
     */
    public deobfuscatev2(inputStr: string): string {
        try {
            const split = (text: string): [string, string] => {
                const len = text.length;
                if (len <= 3) return [text.slice(0, 1), text.slice(1)];
                if (len <= 5) return [text.slice(0, 2), text.slice(2)];
                return [text.slice(0, 3), text.slice(3)];
            };

            let result = "";
            inputStr.split(":").forEach((e, i) => {
                if (e[0] === "s") {
                    const [a, b] = split(e.slice(1));
                    result += String.fromCharCode(Number(a)) + String.fromCharCode(Number(b));
                } else if (e[0] === "e") {
                    result += String.fromCharCode(Number(e.slice(1)));
                } else {
                    const [a, b] = split(String(Number(e) - (i + i * 2)));
                    result += String.fromCharCode(Number(b)) + String.fromCharCode(Number(a));
                }
            });
            return result;
        } catch {
            return "Errored";
        }
    }

    /**
     * V3 obfuscation - binary rotation with hex encoding.
     * @param inputStr String to obfuscate.
     * @param key Key used to determine rotation amounts.
     */
    public obfuscatev3(inputStr: string, key: string): string {
        const keyValues = this._keyToValues(key);
        let kIdx = 0;
        let hex = "";
        const next = (): void => { kIdx = (kIdx + 1) % keyValues.length; };

        const rotr = (bits: string, n: number): string => {
            for (let i = 0; i < n; i++) bits = bits[bits.length - 1] + bits.slice(0, bits.length - 1);
            return bits;
        };

        for (let i = 0; i < inputStr.length; i++) {
            const bin = inputStr.charCodeAt(i).toString(2).padStart(16, "0");
            hex += parseInt(rotr(bin, keyValues[kIdx]), 2).toString(16).padStart(4, "0");
            next();
        }
        return hex;
    }

    /**
     * V3 deobfuscation.
     */
    public deobfuscatev3(inputStr: string, key: string): string {
        const keyValues = this._keyToValues(key);
        let kIdx = 0;
        let result = "";
        const next = (): void => { kIdx = (kIdx + 1) % keyValues.length; };

        const rotl = (bits: string, n: number): string => {
            for (let i = 0; i < n; i++) bits = bits.slice(1) + bits[0];
            return bits;
        };

        const hex2bin = (hex: string): string =>
            parseInt(hex, 16).toString(2).padStart(16, "0");

        for (let i = 0; i < inputStr.length / 4; i++) {
            const chunk = inputStr.slice(i * 4, i * 4 + 4);
            result += String.fromCharCode(parseInt(rotl(hex2bin(chunk), keyValues[kIdx]), 2));
            next();
        }
        return result;
    }

    /**
     * V4 obfuscation - salted keyed byte-stream masking with base64url output.
     * This is reversible obfuscation, not cryptographic encryption.
     */
    public obfuscatev4(inputStr: string, key: string): string {
        this._requireKey(key, "v4");

        const salt = this._randomSalt();
        const bytes = new TextEncoder().encode(inputStr);
        const payload = this._base64UrlEncode(this._applyV4(bytes, key, salt, "encode"));

        return `${this._v4Prefix}:${salt}:${payload}`;
    }

    /**
     * V4 deobfuscation.
     */
    public deobfuscatev4(inputStr: string, key: string): string {
        this._requireKey(key, "v4");

        const parts = inputStr.split(":");
        if (parts.length !== 4 || `${parts[0]}:${parts[1]}` !== this._v4Prefix) {
            throw new Error("Invalid V4 obfuscator payload");
        }

        const [, , salt, payload] = parts;
        const bytes = this._base64UrlDecode(payload);
        const decoded = this._applyV4(bytes, key, salt, "decode");

        return new TextDecoder().decode(decoded);
    }

    private _applyV4(bytes: Uint8Array, key: string, salt: string, mode: "encode" | "decode"): Uint8Array {
        const prng = this._xorshift32(this._seedFromKey(key, salt));
        const result = new Uint8Array(bytes.length);

        for (let i = 0; i < bytes.length; i++) {
            const random = prng();
            const mask = random & 0xff;
            const shift = ((random >>> 8) + i) & 7;

            result[i] = mode === "encode"
                ? this._rotateLeftByte(bytes[i] ^ mask, shift)
                : this._rotateRightByte(bytes[i], shift) ^ mask;
        }

        return result;
    }

    private _seedFromKey(key: string, salt: string): number {
        let hash = 0x811c9dc5;
        const bytes = new TextEncoder().encode(`${salt}:${key}`);

        for (const byte of bytes) {
            hash ^= byte;
            hash = Math.imul(hash, 0x01000193) >>> 0;
        }

        return hash || 0x6d2b79f5;
    }

    private _xorshift32(seed: number): () => number {
        let state = seed >>> 0 || 0x6d2b79f5;

        return (): number => {
            state ^= state << 13;
            state >>>= 0;
            state ^= state >>> 17;
            state >>>= 0;
            state ^= state << 5;
            return state >>> 0;
        };
    }

    private _rotateLeftByte(value: number, shift: number): number {
        return ((value << shift) | (value >>> (8 - shift))) & 0xff;
    }

    private _rotateRightByte(value: number, shift: number): number {
        return ((value >>> shift) | (value << (8 - shift))) & 0xff;
    }

    private _randomSalt(): string {
        const bytes = new Uint8Array(4);

        if (globalThis.crypto?.getRandomValues) {
            globalThis.crypto.getRandomValues(bytes);
        } else {
            for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
        }

        return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
    }

    private _base64UrlEncode(bytes: Uint8Array): string {
        let output = "";

        for (let i = 0; i < bytes.length; i += 3) {
            const b0 = bytes[i];
            const b1 = i + 1 < bytes.length ? bytes[i + 1] : 0;
            const b2 = i + 2 < bytes.length ? bytes[i + 2] : 0;
            const triplet = (b0 << 16) | (b1 << 8) | b2;

            output += this._base64UrlAlphabet[(triplet >>> 18) & 63];
            output += this._base64UrlAlphabet[(triplet >>> 12) & 63];
            if (i + 1 < bytes.length) output += this._base64UrlAlphabet[(triplet >>> 6) & 63];
            if (i + 2 < bytes.length) output += this._base64UrlAlphabet[triplet & 63];
        }

        return output;
    }

    private _base64UrlDecode(input: string): Uint8Array {
        if (input.length % 4 === 1 || /[^A-Za-z0-9_-]/.test(input)) {
            throw new Error("Invalid base64url payload");
        }

        const bytes: number[] = [];

        for (let i = 0; i < input.length; i += 4) {
            const c0 = this._base64UrlValue(input[i]);
            const c1 = this._base64UrlValue(input[i + 1]);
            const c2 = i + 2 < input.length ? this._base64UrlValue(input[i + 2]) : 0;
            const c3 = i + 3 < input.length ? this._base64UrlValue(input[i + 3]) : 0;
            const triplet = (c0 << 18) | (c1 << 12) | (c2 << 6) | c3;

            bytes.push((triplet >>> 16) & 0xff);
            if (i + 2 < input.length) bytes.push((triplet >>> 8) & 0xff);
            if (i + 3 < input.length) bytes.push(triplet & 0xff);
        }

        return new Uint8Array(bytes);
    }

    private _base64UrlValue(char: string | undefined): number {
        if (char === undefined) throw new Error("Invalid base64url payload");

        const value = this._base64UrlAlphabet.indexOf(char);
        if (value === -1) throw new Error("Invalid base64url payload");

        return value;
    }

    private _requireKey(key: string | undefined, version: ObfuscatorVersion): string {
        if (typeof key !== "string" || key.length === 0) {
            throw new Error(`${version.toUpperCase()} obfuscation requires a non-empty key`);
        }

        return key;
    }

    private _keyToValues(key: string): number[] {
        return key.split("").map((c) =>
            String(c.charCodeAt(0)).split("").map(Number).reduce((a, b) => a + b, 0)
        );
    }
}

export const obfuscator = new Obfuscator();
